import { addDays, endOfDay, format, hoursToMilliseconds, startOfDay } from 'date-fns'

import { formatSortedIsoDateStringsRange } from '../../../helpers/date'
import { EstimateValue, PerformanceChange } from '../types/TrackingEvents'

// The day-1 changes and the daily values of one game, as both the tracked-events search and
// `POST /games/tracked/performance` send them. Only these fields are read, so the identifiers the search
// adds (`appId`, `id`) are not required.
export type KpiPerformanceChange = Pick<PerformanceChange, 'date' | 'performanceChanges'>
export type KpiPerformanceValue = Pick<EstimateValue, 'date' | 'revenue' | 'downloads' | 'dailyActiveUsers'>

/**
 * Which calendar the event's days are read in. The calendar works in the viewer's local days; the
 * Adopters tooltip reads UTC days, because the event's durations start and end at UTC midnight and
 * the backend keys its daily data by UTC day.
 */
export type KpiDayBasis = 'local' | 'utc'

type CalculateEventPerformanceChangesParams = {
  eventStart: number
  eventEnd: number
  performanceChanges?: KpiPerformanceChange[]
  performanceValues?: KpiPerformanceValue[]
  dayBasis?: KpiDayBasis
}

type EventPerformanceChanges = {
  REVENUE_DAY_1_CHANGE?: number
  DOWNLOAD_DAY_1_CHANGE?: number
  DAU_DAY_1_CHANGE?: number
}

export type EventKpiPeriodExplanation =
  | {
      variant: 'multi_day_average'
      duringRangeFormatted: string
      beforeRangeFormatted: string
      ongoing: boolean
    }
  | {
      variant: 'single_day_prior'
      eventDayFormatted: string
    }

const average = (values: number[]): number => {
  if (values.length === 0) return 0
  return values.reduce((sum, v) => sum + v, 0) / values.length
}

/**
 * Compute % change between `during` and `before`, returning ±Infinity when
 * the before period was zero but during had movement. Callers can render this
 * via `TrendIndicator allowInfinity` to surface a directional arrow without a
 * misleading "0%" reading.
 */
const computePercentChange = (during: number, before: number): number => {
  if (before !== 0) return (during - before) / before
  if (during > 0) return Infinity
  if (during < 0) return -Infinity
  return 0
}

/**
 * Resolve a single-day day-over-day change for KPI chips. Recovers changes
 * the backend cannot deliver from the daily values in two directions:
 * - yesterday zero → today non-zero: the true change is ±Infinity which cannot
 *   be serialized in JSON, so we surface the direction as ±Infinity ourselves.
 * - yesterday non-zero → today zero, with the backend value missing: the true
 *   change is a full -100% drop, so we return -1 instead of hiding the chip
 *   (GR-2984). A present backend value is never overridden in this direction.
 *
 * Otherwise falls back to the backend-provided value (which may itself be
 * undefined when both days are zero or one of the values is missing).
 */
const resolveDay1Change = (backendValue: number | undefined, todayValue: number | undefined, yesterdayValue: number | undefined): number | undefined => {
  if (typeof todayValue === 'number' && typeof yesterdayValue === 'number') {
    if (yesterdayValue === 0 && todayValue !== 0) {
      return todayValue > 0 ? Infinity : -Infinity
    }

    if (backendValue == null && yesterdayValue !== 0 && todayValue === 0) {
      return -1
    }
  }

  return backendValue
}

const MS_PER_DAY = hoursToMilliseconds(24)

type DayCalendar = {
  startOfDay: (timestamp: number) => number
  endOfDay: (timestamp: number) => number
  addDays: (timestamp: number, days: number) => number
  formatDay: (timestamp: number) => string
}

const localCalendar: DayCalendar = {
  startOfDay: (timestamp) => startOfDay(timestamp).getTime(),
  endOfDay: (timestamp) => endOfDay(timestamp).getTime(),
  addDays: (timestamp, days) => addDays(timestamp, days).getTime(),
  formatDay: (timestamp) => format(timestamp, 'yyyy-MM-dd'),
}

const utcStartOfDay = (timestamp: number) => Math.floor(timestamp / MS_PER_DAY) * MS_PER_DAY

const utcCalendar: DayCalendar = {
  startOfDay: utcStartOfDay,
  endOfDay: (timestamp) => utcStartOfDay(timestamp) + MS_PER_DAY - 1,
  addDays: (timestamp, days) => timestamp + days * MS_PER_DAY,
  formatDay: (timestamp) => new Date(timestamp).toISOString().slice(0, 10),
}

const calendarFor = (dayBasis: KpiDayBasis): DayCalendar => (dayBasis === 'utc' ? utcCalendar : localCalendar)

type KpiDateWindows = {
  duringDates: string[]
  beforeDates: string[]
}

const buildKpiDateWindows = (startTime: number, windowEnd: number, calendar: DayCalendar = localCalendar): KpiDateWindows => {
  const duringDates: string[] = []
  for (let day = 0; calendar.addDays(startTime, day) <= windowEnd; day++) {
    duringDates.push(calendar.formatDay(calendar.addDays(startTime, day)))
  }
  const beforeDates = duringDates.map((_, index) => calendar.formatDay(calendar.addDays(startTime, -(index + 1))))
  return { duringDates, beforeDates }
}

export const getEventKpiPeriodExplanation = ({ eventStart, eventEnd }: CalculateEventPerformanceChangesParams): EventKpiPeriodExplanation | undefined => {
  const startTime = localCalendar.startOfDay(eventStart)
  const endTime = localCalendar.endOfDay(eventEnd)

  const eventDuration = endTime - startTime
  const isMultiDay = eventDuration > MS_PER_DAY
  const eventHasEnded = endTime < Date.now()

  if (isMultiDay) {
    if (startTime > Date.now()) return undefined

    const { duringDates, beforeDates } = buildKpiDateWindows(startTime, endTime)

    return {
      variant: 'multi_day_average',
      duringRangeFormatted: formatSortedIsoDateStringsRange(duringDates),
      beforeRangeFormatted: formatSortedIsoDateStringsRange(beforeDates),
      ongoing: !eventHasEnded,
    }
  } else {
    if (startTime > Date.now()) return undefined
    return {
      variant: 'single_day_prior',
      eventDayFormatted: format(startTime, 'MMM d, yyyy'),
    }
  }
}

/**
 * The days of daily data `calculateEventPerformanceChanges` reads for one event, as the first and last
 * day of a window: the event's own days plus the same number of days before it (the multi-day comparison
 * window; for a single-day event, the day before, which resolves a day-over-day change the backend left out).
 * `undefined` when the calculation shows no KPIs for the event: it has not ended yet (the Adopters tooltip,
 * like the calendar, shows KPIs of ended events only).
 */
export const getEventKpiDataRange = (
  { eventStart, eventEnd, dayBasis = 'local' }: Pick<CalculateEventPerformanceChangesParams, 'eventStart' | 'eventEnd' | 'dayBasis'>,
  now: number = Date.now()
): { from: number; to: number } | undefined => {
  const calendar = calendarFor(dayBasis)
  const startTime = calendar.startOfDay(eventStart)
  const endTime = calendar.endOfDay(eventEnd)
  if (endTime >= now) return undefined

  const { duringDates } = buildKpiDateWindows(startTime, endTime, calendar)
  return { from: calendar.addDays(startTime, -duringDates.length), to: calendar.startOfDay(endTime) }
}

/**
 * The local date whose day is the given timestamp's UTC day, at local midnight. The KPI chip row formats its
 * dates in local time, so UTC-midnight event bounds are handed over as these to show the UTC day they stand for.
 */
export const utcDayAsLocalDate = (timestamp: number): Date => {
  const date = new Date(timestamp)
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

/**
 * Calculates performance changes for an event based on its duration.
 * Only runs for ended events — ongoing events always return undefined.
 * - For multi-day events: avg daily value during event vs avg of same-length window before event
 * - For single-day events: uses existing day-over-day changes from API
 */
export const calculateEventPerformanceChanges = ({
  eventStart,
  eventEnd,
  performanceChanges,
  performanceValues,
  dayBasis = 'local',
}: CalculateEventPerformanceChangesParams): EventPerformanceChanges | undefined => {
  const calendar = calendarFor(dayBasis)
  const startTime = calendar.startOfDay(eventStart)
  const endTime = calendar.endOfDay(eventEnd)

  const eventDuration = endTime - startTime
  const isMultiDay = eventDuration > MS_PER_DAY
  const eventHasEnded = endTime < Date.now()

  if (isMultiDay && performanceValues) {
    if (!eventHasEnded) return undefined

    const { duringDates, beforeDates } = buildKpiDateWindows(startTime, endTime, calendar)

    const valuesByDate: Record<string, KpiPerformanceValue> = Object.fromEntries(performanceValues.map((pv) => [pv.date, pv]))
    const duringValues = duringDates.map((d) => valuesByDate[d]).filter(Boolean) as KpiPerformanceValue[]
    const beforeValues = beforeDates.map((d) => valuesByDate[d]).filter(Boolean) as KpiPerformanceValue[]

    if (duringValues.length === 0 || beforeValues.length === 0) return undefined

    const avgDuringRevenue = average(duringValues.map((pv) => pv.revenue))
    const avgBeforeRevenue = average(beforeValues.map((pv) => pv.revenue))
    const avgDuringDownloads = average(duringValues.map((pv) => pv.downloads))
    const avgBeforeDownloads = average(beforeValues.map((pv) => pv.downloads))

    const duringDau = duringValues.map((pv) => pv.dailyActiveUsers).filter((v): v is number => v !== undefined)
    const beforeDau = beforeValues.map((pv) => pv.dailyActiveUsers).filter((v): v is number => v !== undefined)
    const avgDuringDau = duringDau.length > 0 ? average(duringDau) : undefined
    const avgBeforeDau = beforeDau.length > 0 ? average(beforeDau) : undefined
    const dauChange = avgDuringDau !== undefined && avgBeforeDau !== undefined ? computePercentChange(avgDuringDau, avgBeforeDau) : undefined

    return {
      REVENUE_DAY_1_CHANGE: computePercentChange(avgDuringRevenue, avgBeforeRevenue),
      DOWNLOAD_DAY_1_CHANGE: computePercentChange(avgDuringDownloads, avgBeforeDownloads),
      DAU_DAY_1_CHANGE: dauChange,
    }
  } else if (!isMultiDay && performanceChanges) {
    const eventStartDate = calendar.formatDay(startTime)
    const matchedPerformanceChange = performanceChanges.find((pc) => pc.date === eventStartDate)
    // The performance endpoint (UTC) lists no entry for a day when none of its three changes could be computed, which is
    // exactly the day after a zero day, so there the daily values alone must be able to answer. The search (local) keeps
    // hiding a day without an entry.
    const backendChanges = matchedPerformanceChange?.performanceChanges ?? (dayBasis === 'utc' ? {} : undefined)
    if (!backendChanges) return undefined
    const { REVENUE_DAY_1_CHANGE, DOWNLOAD_DAY_1_CHANGE, DAU_DAY_1_CHANGE } = backendChanges

    // Single-day events use backend's day-over-day change values. When the
    // prior day was 0 (e.g. soft-launched game with no installs yet), backend
    // can't represent ±Infinity in JSON and typically returns null/undefined,
    // which would hide the change chip. Cross-reference performanceValues to
    // recover the direction (today vs yesterday) and emit ±Infinity so the
    // KPI chip renders a directional indicator instead of being skipped.
    const valuesByDate: Record<string, KpiPerformanceValue> = performanceValues ? Object.fromEntries(performanceValues.map((pv) => [pv.date, pv])) : {}
    const today = valuesByDate[eventStartDate]
    const yesterdayDate = calendar.formatDay(startTime - MS_PER_DAY)
    const yesterday = valuesByDate[yesterdayDate]

    return {
      REVENUE_DAY_1_CHANGE: resolveDay1Change(REVENUE_DAY_1_CHANGE, today?.revenue, yesterday?.revenue),
      DOWNLOAD_DAY_1_CHANGE: resolveDay1Change(DOWNLOAD_DAY_1_CHANGE, today?.downloads, yesterday?.downloads),
      DAU_DAY_1_CHANGE: resolveDay1Change(DAU_DAY_1_CHANGE, today?.dailyActiveUsers, yesterday?.dailyActiveUsers),
    }
  }

  return undefined
}
